import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FileItem } from 'components/FileItem'
import {
  IMeetingRoomEquipmentItem,
  IMeetingRoomFile,
  IMeetingRoomFormData,
  IMeetingRoomImage,
} from '../types'
import { MeetingRoomSupportFileItem } from './MeetingRoomSupportFileItem'
import {
  getMeetingRoomPurposeOptions,
  isMeetingRoomCoworkingPurpose,
  MEETING_ROOM_COVER_IMAGES_MAX,
  MEETING_ROOM_DESCRIPTION_MAX_LENGTH,
  MEETING_ROOM_SLIDER_IMAGES_MAX,
} from '../api/constants'
import { trans } from 'sharedKernel/i18n'
import {
  AddLink,
  CardSection,
  EquipmentRow,
  FieldGroup,
  DescriptionCharCount,
  FieldLabel,
  FieldRow,
  FormHeader,
  FormTitle,
  ImageNoteText,
  ImagePreview,
  ImagePreviewRemoveButton,
  ImagePreviewWrapper,
  ImagesUploadRow,
  MeetingRoomFormWrapper,
  RemoveIconButton,
  RequiredMark,
  SectionLabel,
  UploadBtn,
} from './styles'

type NewImagePreview = {
  file: File
  previewUrl: string
}

interface MeetingRoomFormProps {
  initialData?: Partial<IMeetingRoomFormData>
  onSave: (data: IMeetingRoomFormData) => Promise<void>
  isEditing?: boolean
  isSaving?: boolean
  meetingRoomId?: string
}

const getDuplicateEquipmentIndices = (equipment: IMeetingRoomEquipmentItem[]): Set<number> => {
  const byNormalizedName = new Map<string, number[]>()

  equipment.forEach((item, index) => {
    const key = item.name.trim().toLowerCase()
    if (!key) return
    const indices = byNormalizedName.get(key) ?? []
    indices.push(index)
    byNormalizedName.set(key, indices)
  })

  const duplicates = new Set<number>()
  byNormalizedName.forEach(indices => {
    if (indices.length > 1) {
      indices.slice(1).forEach(index => duplicates.add(index))
    }
  })

  return duplicates
}

const equipmentErrorKey = (index: number): string => `equipment_${index}`

const defaultFormData: IMeetingRoomFormData = {
  name: '',
  purpose: '',
  description: '',
  maxCapacity: '',
  chairQuantity: '',
  equipment: [{ name: '' }],
  coverImages: [],
  sliderImages: [],
  rulesFile: null,
}

export const MeetingRoomForm = ({
  initialData,
  onSave,
  isEditing = false,
  isSaving: isSavingExternal = false,
  meetingRoomId,
}: MeetingRoomFormProps): JSX.Element => {
  const purposeOptions = useMemo(() => getMeetingRoomPurposeOptions(), [])

  const [formData, setFormData] = useState<IMeetingRoomFormData>({
    ...defaultFormData,
    ...initialData,
  })
  const [newCoverPreviews, setNewCoverPreviews] = useState<NewImagePreview[]>(() =>
    (initialData?.coverImages ?? []).map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }))
  )
  const [existingCoverImages, setExistingCoverImages] = useState<IMeetingRoomImage[]>(
    initialData?.existingCoverImages ?? []
  )
  const [removedCoverImageIds, setRemovedCoverImageIds] = useState<string[]>(
    initialData?.removedCoverImageIds ?? []
  )
  const [newSliderPreviews, setNewSliderPreviews] = useState<NewImagePreview[]>(() =>
    (initialData?.sliderImages ?? []).map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }))
  )
  const [existingSliderImages, setExistingSliderImages] = useState<IMeetingRoomImage[]>(
    initialData?.existingSliderImages ?? []
  )
  const [removedSliderImageIds, setRemovedSliderImageIds] = useState<string[]>(
    initialData?.removedSliderImageIds ?? []
  )
  const [rulesFile, setRulesFile] = useState<File | null>(initialData?.rulesFile ?? null)
  const [existingSupportFile, setExistingSupportFile] = useState<IMeetingRoomFile | undefined>(
    initialData?.existingSupportFiles?.[0]
  )
  const [removedSupportFileIds, setRemovedSupportFileIds] = useState<string[]>(
    initialData?.removedSupportFileIds ?? []
  )
  const [isSavingLocal, setIsSavingLocal] = useState(false)
  const isSaving = isSavingExternal || isSavingLocal
  const isBusy = isSaving
  const [errors, setErrors] = useState<Record<string, string>>({})

  const imageInputRef = useRef<HTMLInputElement>(null)
  const sliderInputRef = useRef<HTMLInputElement>(null)
  const rulesInputRef = useRef<HTMLInputElement>(null)
  const saveInFlightRef = useRef(false)
  const newCoverPreviewsRef = useRef(newCoverPreviews)
  const newSliderPreviewsRef = useRef(newSliderPreviews)
  newCoverPreviewsRef.current = newCoverPreviews
  newSliderPreviewsRef.current = newSliderPreviews

  useEffect(() => {
    return () => {
      newCoverPreviewsRef.current.forEach(item => {
        if (item.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl)
        }
      })
      newSliderPreviewsRef.current.forEach(item => {
        if (item.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl)
        }
      })
    }
  }, [])

  const isCoworking = isMeetingRoomCoworkingPurpose(formData.purpose)
  const visibleExistingCoverImages = existingCoverImages.filter(
    img => !removedCoverImageIds.includes(img.id)
  )
  const visibleExistingSliderImages = existingSliderImages.filter(
    img => !removedSliderImageIds.includes(img.id)
  )
  const coverImageFiles = newCoverPreviews.map(item => item.file)
  const sliderImageFiles = newSliderPreviews.map(item => item.file)
  const hasCoverImage =
    visibleExistingCoverImages.length > 0 || newCoverPreviews.length > 0
  const totalCoverImages = visibleExistingCoverImages.length + newCoverPreviews.length
  const totalSliderImages = visibleExistingSliderImages.length + newSliderPreviews.length
  const isCoverMaxReached = totalCoverImages >= MEETING_ROOM_COVER_IMAGES_MAX
  const isSliderMaxReached = totalSliderImages >= MEETING_ROOM_SLIDER_IMAGES_MAX
  const visibleExistingSupportFile =
    existingSupportFile != null && !removedSupportFileIds.includes(existingSupportFile.id)
      ? existingSupportFile
      : undefined
  const hasSupportFile = rulesFile != null || visibleExistingSupportFile != null

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) newErrors.name = trans('meetingRoom.form.validation.nameRequired')
    if (!formData.purpose) newErrors.purpose = trans('meetingRoom.form.validation.purposeRequired')
    if (!formData.description.trim()) {
      newErrors.description = trans('meetingRoom.form.validation.descriptionRequired')
    } else if (formData.description.length > MEETING_ROOM_DESCRIPTION_MAX_LENGTH) {
      newErrors.description = trans('meetingRoom.form.validation.descriptionMaxLength')
    }
    if (isCoworking) {
      if (!formData.chairQuantity || Number(formData.chairQuantity) <= 0) {
        newErrors.chairQuantity = trans('meetingRoom.form.validation.chairQuantityRequired')
      }
    } else if (!formData.maxCapacity || Number(formData.maxCapacity) <= 0) {
      newErrors.maxCapacity = trans('meetingRoom.form.validation.maxCapacityRequired')
    }
    if (!hasCoverImage) {
      newErrors.coverImages = trans('meetingRoom.form.validation.coverImageRequired')
    }
    const duplicateMessage = trans('meetingRoom.form.validation.equipmentDuplicate')
    getDuplicateEquipmentIndices(formData.equipment).forEach(index => {
      newErrors[equipmentErrorKey(index)] = duplicateMessage
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const syncEquipmentDuplicateErrors = (equipment: IMeetingRoomEquipmentItem[]) => {
    const duplicateMessage = trans('meetingRoom.form.validation.equipmentDuplicate')
    const duplicateIndices = getDuplicateEquipmentIndices(equipment)

    setErrors(prev => {
      const next = { ...prev }
      Object.keys(next).forEach(key => {
        if (key.startsWith('equipment_')) delete next[key]
      })
      duplicateIndices.forEach(index => {
        next[equipmentErrorKey(index)] = duplicateMessage
      })
      return next
    })
  }

  const handleSave = async () => {
    if (!validate() || saveInFlightRef.current || isBusy) return

    saveInFlightRef.current = true
    setIsSavingLocal(true)
    try {
      await onSave({
        ...formData,
        coverImages: coverImageFiles,
        sliderImages: sliderImageFiles,
        rulesFile,
        existingCoverImages,
        existingSliderImages,
        existingSupportFiles: existingSupportFile ? [existingSupportFile] : (initialData?.existingSupportFiles ?? []),
        removedCoverImageIds,
        removedSliderImageIds,
        removedSupportFileIds,
      })
    } catch {
    } finally {
      saveInFlightRef.current = false
      setIsSavingLocal(false)
    }
  }

  const handleAddEquipment = () => {
    setFormData(prev => ({ ...prev, equipment: [...prev.equipment, { name: '' }] }))
  }

  const handleEquipmentChange = (index: number, value: string) => {
    const updated = formData.equipment.map((item, i) =>
      i === index ? { ...item, name: value } : item
    )
    setFormData(prev => ({ ...prev, equipment: updated }))
    syncEquipmentDuplicateErrors(updated)
  }

  const handleRemoveEquipment = (index: number) => {
    const updated = formData.equipment.filter((_, i) => i !== index)
    const nextEquipment = updated.length > 0 ? updated : [{ name: '' }]
    setFormData(prev => ({ ...prev, equipment: nextEquipment }))
    syncEquipmentDuplicateErrors(nextEquipment)
  }

  const handleAddCoverImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (!file) return

    setNewCoverPreviews(prev => {
      prev.forEach(item => {
        if (item.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl)
        }
      })
      return [{ file, previewUrl: URL.createObjectURL(file) }]
    })
    if (errors.coverImages) setErrors(prev => ({ ...prev, coverImages: '' }))
    e.target.value = ''
  }

  const handleRemoveNewCoverImage = (index: number) => {
    setNewCoverPreviews(prev => {
      const removed = prev[index]
      if (removed?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(removed.previewUrl)
      }
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleRemoveExistingCoverImage = (imageId: string) => {
    setRemovedCoverImageIds(prev => [...prev, imageId])
  }

  const handleAddSliderImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return

    setNewSliderPreviews(prev => {
      const visibleExistingCount = existingSliderImages.filter(
        img => !removedSliderImageIds.includes(img.id)
      ).length
      const availableSlots = Math.max(
        MEETING_ROOM_SLIDER_IMAGES_MAX - visibleExistingCount - prev.length,
        0
      )
      const filesToAdd = files.slice(0, availableSlots)
      const newItems: NewImagePreview[] = filesToAdd.map(file => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }))
      return [...prev, ...newItems]
    })
    e.target.value = ''
  }

  const handleRemoveNewSliderImage = (index: number) => {
    setNewSliderPreviews(prev => {
      const removed = prev[index]
      if (removed?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(removed.previewUrl)
      }
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleRemoveExistingSliderImage = (imageId: string) => {
    setRemovedSliderImageIds(prev => [...prev, imageId])
  }

  const handleAddRules = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (!file) return

    const previousSupportFile =
      existingSupportFile != null && !removedSupportFileIds.includes(existingSupportFile.id)
        ? existingSupportFile
        : undefined

    if (previousSupportFile != null) {
      setRemovedSupportFileIds(prev =>
        prev.includes(previousSupportFile.id) ? prev : [...prev, previousSupportFile.id]
      )
    }

    setRulesFile(file)
    e.target.value = ''
  }

  return (
    <MeetingRoomFormWrapper>
      <FormHeader>
        <FormTitle>{isEditing ? trans('meetingRoom.form.editTitle') : trans('meetingRoom.form.newTitle')}</FormTitle>
        <Button variant="primary" onClick={handleSave} disabled={isBusy}>
          {isSaving ? trans('meetingRoom.form.saving') : trans('meetingRoom.form.save')}
        </Button>
      </FormHeader>

      <CardSection>
        <FieldRow>
          <FieldGroup>
            <FieldLabel>
              {trans('meetingRoom.form.name')}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <Form.Control
              type="text"
              placeholder={trans('meetingRoom.form.namePlaceholder')}
              value={formData.name}
              isInvalid={!!errors.name}
              onChange={e => {
                setFormData(prev => ({ ...prev, name: e.target.value }))
                if (errors.name) setErrors(prev => ({ ...prev, name: '' }))
              }}
            />
            {errors.name && (
              <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>
              {trans('meetingRoom.form.purpose')}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <Form.Select
              value={formData.purpose}
              isInvalid={!!errors.purpose}
              onChange={e => {
                setFormData(prev => ({ ...prev, purpose: e.target.value }))
                if (errors.purpose) setErrors(prev => ({ ...prev, purpose: '' }))
              }}
            >
              {purposeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            {errors.purpose && (
              <Form.Control.Feedback type="invalid">{errors.purpose}</Form.Control.Feedback>
            )}
          </FieldGroup>
        </FieldRow>

        <FieldGroup>
          <FieldLabel>
            {trans('meetingRoom.form.description')}
            <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={MEETING_ROOM_DESCRIPTION_MAX_LENGTH}
            value={formData.description}
            isInvalid={!!errors.description}
            onChange={e => {
              const description = e.target.value.slice(0, MEETING_ROOM_DESCRIPTION_MAX_LENGTH)
              setFormData(prev => ({ ...prev, description }))
              if (errors.description) setErrors(prev => ({ ...prev, description: '' }))
            }}
          />
          <DescriptionCharCount
            $nearLimit={formData.description.length >= MEETING_ROOM_DESCRIPTION_MAX_LENGTH}
          >
            {formData.description.length}/{MEETING_ROOM_DESCRIPTION_MAX_LENGTH}
          </DescriptionCharCount>
          {errors.description && (
            <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
          )}
        </FieldGroup>

        {isCoworking ? (
          <FieldGroup>
            <FieldLabel>
              {trans('meetingRoom.form.chairQuantity')}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <Form.Control
              type="number"
              min={1}
              placeholder={trans('meetingRoom.form.chairQuantityPlaceholder')}
              value={formData.chairQuantity ?? ''}
              isInvalid={!!errors.chairQuantity}
              style={{ maxWidth: '400px' }}
              onChange={e => {
                setFormData(prev => ({ ...prev, chairQuantity: e.target.value }))
                if (errors.chairQuantity) setErrors(prev => ({ ...prev, chairQuantity: '' }))
              }}
            />
            {errors.chairQuantity && (
              <Form.Control.Feedback type="invalid">{errors.chairQuantity}</Form.Control.Feedback>
            )}
          </FieldGroup>
        ) : (
          <FieldGroup>
            <FieldLabel>
              {trans('meetingRoom.form.maxCapacity')}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <Form.Control
              type="number"
              min={1}
              placeholder={trans('meetingRoom.form.maxCapacityPlaceholder')}
              value={formData.maxCapacity}
              isInvalid={!!errors.maxCapacity}
              style={{ maxWidth: '400px' }}
              onChange={e => {
                setFormData(prev => ({ ...prev, maxCapacity: e.target.value }))
                if (errors.maxCapacity) setErrors(prev => ({ ...prev, maxCapacity: '' }))
              }}
            />
            {errors.maxCapacity && (
              <Form.Control.Feedback type="invalid">{errors.maxCapacity}</Form.Control.Feedback>
            )}
          </FieldGroup>
        )}

        <FieldGroup>
          <SectionLabel>{trans('meetingRoom.form.equipmentSection')}</SectionLabel>
          {formData.equipment.map((eq, index) => {
            const equipmentError = errors[equipmentErrorKey(index)]
            return (
            <EquipmentRow key={index}>
              <div style={{ maxWidth: '400px', flex: 1 }}>
                <Form.Control
                  type="text"
                  placeholder={trans('meetingRoom.form.equipmentPlaceholder')}
                  value={eq.name}
                  isInvalid={!!equipmentError}
                  onChange={e => handleEquipmentChange(index, e.target.value)}
                />
                {equipmentError && (
                  <Form.Control.Feedback type="invalid">{equipmentError}</Form.Control.Feedback>
                )}
              </div>
              {eq.name.trim() !== '' && (
                <RemoveIconButton
                  type="button"
                  onClick={() => handleRemoveEquipment(index)}
                  title={trans('meetingRoom.form.remove')}
                >
                  <FontAwesomeIcon icon={['fas', 'xmark']} />
                </RemoveIconButton>
              )}
            </EquipmentRow>
            )
          })}
          <AddLink type="button" onClick={handleAddEquipment}>
            {trans('meetingRoom.form.addEquipment')}
          </AddLink>
        </FieldGroup>

        <FieldGroup>
          <SectionLabel>
            {trans('meetingRoom.form.coverImageSection')}
            <RequiredMark>*</RequiredMark>
          </SectionLabel>
          <ImageNoteText>{trans('meetingRoom.form.coverImageHint')}</ImageNoteText>
          {errors.coverImages && (
            <div style={{ color: '#dc3545', fontSize: '0.8rem', marginBottom: '8px' }}>
              {errors.coverImages}
            </div>
          )}
          <ImagesUploadRow>
            {visibleExistingCoverImages.map(img => (
              <ImagePreviewWrapper key={img.id}>
                <ImagePreview src={img.path} alt={img.name} />
                <ImagePreviewRemoveButton
                  type="button"
                  onClick={() => handleRemoveExistingCoverImage(img.id)}
                  title={trans('meetingRoom.form.removeImage')}
                  aria-label={trans('meetingRoom.form.removeImage')}
                >
                  ×
                </ImagePreviewRemoveButton>
              </ImagePreviewWrapper>
            ))}
            {newCoverPreviews.map((item, index) => (
              <ImagePreviewWrapper key={`${item.file.name}-${index}-${item.previewUrl}`}>
                <ImagePreview src={item.previewUrl} alt={item.file.name} />
                <ImagePreviewRemoveButton
                  type="button"
                  onClick={() => handleRemoveNewCoverImage(index)}
                  title={trans('meetingRoom.form.removeImage')}
                  aria-label={trans('meetingRoom.form.removeImage')}
                >
                  ×
                </ImagePreviewRemoveButton>
              </ImagePreviewWrapper>
            ))}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAddCoverImage}
            />
            {!isCoverMaxReached && (
              <UploadBtn
                type="button"
                disabled={isBusy}
                onClick={() => imageInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} style={{ marginRight: '6px' }} />
                {trans('meetingRoom.form.addImage')}
              </UploadBtn>
            )}
          </ImagesUploadRow>
        </FieldGroup>

        <FieldGroup>
          <SectionLabel>{trans('meetingRoom.form.sliderImagesSection')}</SectionLabel>
          <ImageNoteText>{trans('meetingRoom.form.sliderImagesHint')}</ImageNoteText>
          <ImagesUploadRow>
            {visibleExistingSliderImages.map(img => (
              <ImagePreviewWrapper key={img.id}>
                <ImagePreview src={img.path} alt={img.name} />
                <ImagePreviewRemoveButton
                  type="button"
                  onClick={() => handleRemoveExistingSliderImage(img.id)}
                  title={trans('meetingRoom.form.removeImage')}
                  aria-label={trans('meetingRoom.form.removeImage')}
                >
                  ×
                </ImagePreviewRemoveButton>
              </ImagePreviewWrapper>
            ))}
            {newSliderPreviews.map((item, index) => (
              <ImagePreviewWrapper key={`${item.file.name}-${index}-${item.previewUrl}`}>
                <ImagePreview src={item.previewUrl} alt={item.file.name} />
                <ImagePreviewRemoveButton
                  type="button"
                  onClick={() => handleRemoveNewSliderImage(index)}
                  title={trans('meetingRoom.form.removeImage')}
                  aria-label={trans('meetingRoom.form.removeImage')}
                >
                  ×
                </ImagePreviewRemoveButton>
              </ImagePreviewWrapper>
            ))}
            <input
              ref={sliderInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleAddSliderImages}
            />
            {!isSliderMaxReached && (
              <UploadBtn
                type="button"
                disabled={isBusy}
                onClick={() => sliderInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} style={{ marginRight: '6px' }} />
                {trans('meetingRoom.form.addImage')}
              </UploadBtn>
            )}
            {isSliderMaxReached && (
              <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                {trans('meetingRoom.form.addImage')} ({totalSliderImages}/{MEETING_ROOM_SLIDER_IMAGES_MAX})
              </div>
            )}
          </ImagesUploadRow>
        </FieldGroup>

        <FieldGroup>
          <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem', color: '#222' }}>
            {trans('meetingRoom.form.addFilesLabel')}
          </Form.Label>
          <ImageNoteText>{trans('meetingRoom.form.addFilesHint')}</ImageNoteText>

          <div className="mb-3">
          {visibleExistingSupportFile && !rulesFile && (
            <MeetingRoomSupportFileItem
              fileName={
                visibleExistingSupportFile.originalName ??
                visibleExistingSupportFile.path
              }
              fileUrl={visibleExistingSupportFile.path}
              mimeType={visibleExistingSupportFile.mimeType}
              onRemove={() => {
                setRemovedSupportFileIds(prev =>
                  prev.includes(visibleExistingSupportFile.id)
                    ? prev
                    : [...prev, visibleExistingSupportFile.id]
                )
              }}
            />
          )}

          {rulesFile && (
            <FileItem file={rulesFile} onRemove={() => setRulesFile(null)} />
          )}
          </div>

          <input
            ref={rulesInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: 'none' }}
            onChange={handleAddRules}
          />
          {!hasSupportFile && (
            <UploadBtn
              type="button"
              disabled={isBusy}
              onClick={() => rulesInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} style={{ marginRight: '6px' }} />
              {trans('meetingRoom.form.addFile')}
            </UploadBtn>
          )}
        </FieldGroup>
      </CardSection>
    </MeetingRoomFormWrapper>
  )
}
